import { z } from 'zod/v4';

import { StudioExecutionColumnTypes, StudioVisualizationType } from '@blockworks/platform/api/research/studio';

export const VisualizationTypeSchema = z.nativeEnum(StudioVisualizationType);

export type VisualizationType = z.infer<typeof VisualizationTypeSchema>;

export const BasicColumnTypeSchema = z.enum([
    'date',
    'number',
    'string',
    'double',
    'smallint',
    'integer',
    'bigint',
    'numeric',
    'real',
    'decimal',
    'double precision',
    'timestamp with time zone',
    'text',
]);

export const ExecutionMetadataSchema = z.object({
    rowCount: z.number().optional(),
    columnTypes: z.record(z.string(), BasicColumnTypeSchema),
    dimensions: z.record(z.string(), z.any()).optional(),
});

export const ExecutionDataSchema = z.object({
    id: z.string(),
    rows: z.array(z.any()),
    metadata: ExecutionMetadataSchema,
    error: z.string().optional(),
});

export const BaseVisualizationArgsSchema = z.object({
    id: z.string(),
    type: VisualizationTypeSchema,
    title: z.string(),
    description: z.string().optional().nullable(),
    editing: z.boolean().optional(),
    params: z.record(z.string(), z.string()).default({}),
    activeControls: z.record(z.string(), z.any()).optional(),
    execution: ExecutionDataSchema,
    config: z.object({
        id: z.string().optional(),
        options: z.any(),
    }),
});

export const createVisualizationResponseSchema = <T extends VisualizationType>(type: T) =>
    z.object({
        id: z.string(),
        executionId: z.string(),
        type: z.literal<T>(type),
        title: z.string(),
        description: z.string().optional(),
        editing: z.boolean().optional(),
        meta: ExecutionMetadataSchema.optional(),
        options: z.any(),
        data: z.any(),
        params: z.record(z.string(), z.string()).optional(),
        activeControls: z.record(z.string(), z.any()).optional(),
    });

export type BaseVisualizationArgs = z.infer<typeof BaseVisualizationArgsSchema>;
export type ExecutionData = z.infer<typeof ExecutionDataSchema>;

// Generic ExecutionMetadata interface that tracks column names
export interface ExecutionMetadata<TColumns extends string = string> {
    rowCount?: number;
    columnTypes: Map<TColumns, StudioExecutionColumnTypes> | Record<TColumns, StudioExecutionColumnTypes>;
    dimensions?: Record<string, any>;
}

// Type for the raw parsed metadata (non-generic)
export type ExecutionMetadataRaw = z.infer<typeof ExecutionMetadataSchema>;
